import { useMemo, useState } from 'react';
import { IconAlert, IconCalendar, IconCheck, IconClock, IconPlus } from '../../components/icons';
import { RowActions } from '../../components/RowActions';
import {
  Badge,
  Card,
  EmptyState,
  ErrorMessage,
  Field,
  Loading,
  Modal,
  ModuleHeader,
  SearchSelect,
  StatCard,
  Toggle,
  useAsync,
  useToast,
} from '../../components/ui';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { formatDate, formatDateTime } from '../../lib/format';
import { WEEKDAYS } from '../../lib/weekdays';

const ABSENCE_TYPES: Record<string, string> = {
  urlaub: 'Urlaub',
  schulung: 'Schulung',
  sonstiges: 'Sonstiges',
};

interface DayHours {
  enabled: boolean;
  from: string;
  to: string;
}
type ServiceHours = Record<string, DayHours>;

const DEFAULT_DAY: DayHours = { enabled: false, from: '07:00', to: '17:00' };

/** Altes, gemeinsames Zeitfenster vor dem Umbau auf individuelle Tageszeiten. */
interface LegacyServiceHours {
  from?: string;
  to?: string;
  days?: number[];
}

function isLegacyFormat(raw: unknown): raw is LegacyServiceHours {
  return Boolean(raw) && typeof raw === 'object' && Array.isArray((raw as LegacyServiceHours).days);
}

/**
 * Bringt den gespeicherten Wert auf die neue, pro Tag einzelne Form.
 *
 * Bestehende Installationen haben noch das alte gemeinsame Zeitfenster
 * (`{from, to, days: [1,2,3,4,5]}`) gespeichert. Ohne diese Übernahme zeigte
 * die neue Ansicht „kein Servicetag gewählt“ – ein bestehender Betrieb
 * verlöre so seine Servicezeiten beim ersten Aufruf nach dem Umbau, bis
 * jemand sie von Hand neu einträgt. Die alte Angabe wird deshalb einmalig in
 * die neue Form übersetzt; erst ein Speichern schreibt sie tatsächlich um.
 */
function normalizeServiceHours(raw: unknown): ServiceHours {
  const legacy = isLegacyFormat(raw) ? raw : null;
  // Die alte Ansicht zählte Sonntag als 0 statt (ISO-gerecht) als 7 – dieselbe
  // Uneinheitlichkeit, die der Umbau beheben soll, muss beim Übernehmen alter
  // Werte aber trotzdem berücksichtigt werden.
  const legacyDays = (legacy?.days ?? []).map((day) => (day === 0 ? 7 : day));
  const source = (legacy ? {} : (raw ?? {})) as Record<string, Partial<DayHours>>;
  const result: ServiceHours = {};
  for (const day of WEEKDAYS) {
    if (legacy) {
      result[String(day.number)] = {
        enabled: legacyDays.includes(day.number),
        from: legacy.from ?? DEFAULT_DAY.from,
        to: legacy.to ?? DEFAULT_DAY.to,
      };
      continue;
    }
    const stored = source[String(day.number)];
    result[String(day.number)] = {
      enabled: stored?.enabled ?? false,
      from: stored?.from ?? DEFAULT_DAY.from,
      to: stored?.to ?? DEFAULT_DAY.to,
    };
  }
  return result;
}

/** Servicezeiten als lesbarer Satz, etwa „Mo–Fr 07:00–17:00 Uhr, Sa 08:00–12:00 Uhr“. */
function describeServiceHours(hours: ServiceHours): string {
  const enabledDays = WEEKDAYS.filter((day) => hours[String(day.number)]?.enabled);
  if (enabledDays.length === 0) return 'kein Servicetag gewählt';

  const segments: string[] = [];
  let start = enabledDays[0];
  let previous = enabledDays[0];
  const flush = (last: typeof start) => {
    const startHours = hours[String(start.number)];
    const dayText =
      last.number === start.number ? start.short : `${start.short}–${last.short}`;
    segments.push(`${dayText} ${startHours.from}–${startHours.to} Uhr`);
  };
  for (const day of enabledDays.slice(1)) {
    const current = hours[String(day.number)];
    const startHours = hours[String(start.number)];
    const contiguous =
      day.number === previous.number + 1 && current.from === startHours.from && current.to === startHours.to;
    if (!contiguous) {
      flush(previous);
      start = day;
    }
    previous = day;
  }
  flush(previous);
  return segments.join(', ');
}

/**
 * Betriebskalender – jetzt Teil der Systemeinstellungen (Kap. 4/9).
 *
 * Servicezeiten, Feiertage und Abwesenheiten beantworten dieselbe Frage: wann
 * wird gearbeitet und wer ist da? Der Menüpunkt „Betriebskalender“ führt
 * weiterhin hierher, nur die Adresse hat sich geändert. Die Abwesenheiten
 * hier zeigen den Bestand aller Personen (Admin-Sicht) – die eigene
 * Selbstverwaltung liegt im Profil.
 */
export function OperationalCalendar() {
  const { can } = useAuth();
  const { notify } = useToast();
  const [section, setSection] = useState<'zeiten' | 'feiertage' | 'abwesenheiten'>('zeiten');

  const settings = useAsync(() => api.get('/api/catalogs/settings'), []);
  const holidays = useAsync(() => api.get('/api/catalogs/holidays').catch(() => []), []);
  const absences = useAsync(() => api.get('/api/catalogs/absences').catch(() => []), []);
  const users = useAsync(() => api.get('/api/users').catch(() => []), []);
  const holidaySubscription = useAsync(() => api.get('/api/catalogs/holidays/subscription').catch(() => null), []);
  const absenceSubscriptions = useAsync(() => api.get('/api/catalogs/absences/subscriptions').catch(() => []), []);

  const [modal, setModal] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const [holidayIcalUrl, setHolidayIcalUrl] = useState('');
  const [holidaySubBusy, setHolidaySubBusy] = useState(false);

  const mayEditCatalog = can('admin.catalogs.manage');
  const mayEditAbsences = can('dispatch.absences');

  const saveHolidaySubscription = async () => {
    if (!holidayIcalUrl.trim()) return;
    setHolidaySubBusy(true);
    try {
      const result = await api.put('/api/catalogs/holidays/subscription', { icalUrl: holidayIcalUrl.trim() });
      if (result.ok) {
        notify(`Feiertagskalender abonniert, ${result.count ?? 0} Termin(e) übernommen.`, 'success');
        setHolidayIcalUrl('');
        holidaySubscription.reload();
        holidays.reload();
      } else {
        notify(`Der Kalender ließ sich nicht abrufen: ${result.error ?? 'unbekannter Fehler'}`, 'error');
      }
    } catch (caught) {
      setError(caught);
    } finally {
      setHolidaySubBusy(false);
    }
  };

  const removeHolidaySubscription = async () => {
    if (!window.confirm('Feiertagskalender-Abo entfernen? Die daraus übernommenen Feiertage werden mit entfernt.')) return;
    await api.delete('/api/catalogs/holidays/subscription');
    holidaySubscription.reload();
    holidays.reload();
    notify('Abo entfernt.', 'success');
  };

  const [draft, setDraft] = useState<ServiceHours | null>(null);
  const hours = draft ?? normalizeServiceHours(settings.data?.['service.hours']);
  const setDay = (dayNumber: number, patch: Partial<DayHours>) =>
    setDraft({ ...hours, [String(dayNumber)]: { ...hours[String(dayNumber)], ...patch } });

  const userList = users.data?.items ?? users.data ?? [];

  const saveHours = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.put('/api/catalogs/settings', { 'service.hours': hours });
      notify('Servicezeiten gespeichert.', 'success');
      settings.reload();
      setDraft(null);
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  /** Kommende Feiertage zuerst – vergangene interessieren im Alltag nicht. */
  const upcomingHolidays = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return [...(holidays.data ?? [])].sort((a: any, b: any) => {
      const aPast = a.date < today;
      const bPast = b.date < today;
      if (aPast !== bPast) return aPast ? 1 : -1;
      return a.date.localeCompare(b.date);
    });
  }, [holidays.data]);

  const runningAbsences = (absences.data ?? []).filter((entry: any) => {
    const today = new Date().toISOString().slice(0, 10);
    return entry.start_date <= today && entry.end_date >= today;
  });

  return (
    <div className="stack">
      <ModuleHeader
        icon={<IconCalendar width={22} height={22} />}
        title="Betriebskalender"
        subtitle="Servicezeiten, Feiertage und Abwesenheiten – alles, was bestimmt, wann gearbeitet wird und wer verfügbar ist. Fristen von SLA-Stufen mit Bindung an Geschäftszeiten rechnen genau hiernach."
        pill="Konfiguration"
        tone="info"
      />

      <Card
        actions={
          <div className="row" style={{ gap: 8 }}>
            <button
              className={`btn sm ${section === 'zeiten' ? 'primary' : ''}`}
              onClick={() => setSection('zeiten')}
            >
              <IconClock width={14} height={14} /> Servicezeiten
            </button>
            <button
              className={`btn sm ${section === 'feiertage' ? 'primary' : ''}`}
              onClick={() => setSection('feiertage')}
            >
              <IconCalendar width={14} height={14} /> Feiertage
            </button>
            <button
              className={`btn sm ${section === 'abwesenheiten' ? 'primary' : ''}`}
              onClick={() => setSection('abwesenheiten')}
            >
              <IconAlert width={14} height={14} /> Abwesenheiten
            </button>
          </div>
        }
      >
        <ErrorMessage error={error} />

        {section === 'zeiten' &&
          (settings.loading ? (
            <Loading />
          ) : (
            <div className="stack">
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Tag</th>
                      <th>Aktiv</th>
                      <th>Beginn</th>
                      <th>Ende</th>
                    </tr>
                  </thead>
                  <tbody>
                    {WEEKDAYS.map((day) => {
                      const value = hours[String(day.number)];
                      return (
                        <tr key={day.number}>
                          <td>{day.label}</td>
                          <td>
                            <Toggle
                              checked={value.enabled}
                              disabled={!mayEditCatalog}
                              label={null}
                              onChange={(checked) => setDay(day.number, { enabled: checked })}
                            />
                          </td>
                          <td>
                            <input
                              type="time"
                              value={value.from}
                              disabled={!mayEditCatalog || !value.enabled}
                              onChange={(event) => setDay(day.number, { from: event.target.value })}
                            />
                          </td>
                          <td>
                            <input
                              type="time"
                              value={value.to}
                              disabled={!mayEditCatalog || !value.enabled}
                              onChange={(event) => setDay(day.number, { to: event.target.value })}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="note-box tone-info">
                <IconClock width={15} height={15} />
                <span>
                  <strong>Aktuell:</strong> {describeServiceHours(hours)}
                  {upcomingHolidays.length > 0 &&
                    ` Zusätzlich sind ${upcomingHolidays.length} Feiertag(e) hinterlegt, an denen Fristen ruhen.`}
                </span>
              </div>

              {mayEditCatalog && (
                <div className="row end">
                  <button className="btn primary" onClick={saveHours} disabled={busy || !draft}>
                    Servicezeiten speichern
                  </button>
                </div>
              )}
            </div>
          ))}

        {section === 'feiertage' && (
          <div className="stack">
            {mayEditCatalog && (
              <div className="note-box tone-info">
                <IconCalendar width={15} height={15} />
                {holidaySubscription.data ? (
                  <span className="stack" style={{ gap: 6, flex: 1 }}>
                    <span>
                      Feiertagskalender abonniert: <span className="mono small">{holidaySubscription.data.ical_url}</span>
                      {holidaySubscription.data.last_error ? (
                        <span style={{ marginLeft: 8 }}>
                          <Badge variant="danger">{holidaySubscription.data.last_error}</Badge>
                        </span>
                      ) : holidaySubscription.data.last_synced_at ? (
                        <span className="small subtle" style={{ marginLeft: 8 }}>
                          zuletzt abgeglichen {formatDateTime(holidaySubscription.data.last_synced_at)}
                        </span>
                      ) : null}
                    </span>
                    <span className="row">
                      <button className="btn sm danger" onClick={removeHolidaySubscription}>
                        Abo entfernen
                      </button>
                    </span>
                  </span>
                ) : (
                  <span className="row" style={{ gap: 8, flex: 1, flexWrap: 'wrap' }}>
                    <span style={{ marginRight: 'auto' }}>
                      Feiertage lassen sich zusätzlich über eine Kalender-URL abonnieren (z. B. ein öffentlicher
                      Feiertagskalender) – wird automatisch abgeglichen.
                    </span>
                    <input
                      type="url"
                      placeholder="https://…/feiertage.ics"
                      value={holidayIcalUrl}
                      onChange={(event) => setHolidayIcalUrl(event.target.value)}
                      style={{ minWidth: 280 }}
                    />
                    <button
                      className="btn primary sm"
                      disabled={holidaySubBusy || !holidayIcalUrl.trim()}
                      onClick={saveHolidaySubscription}
                    >
                      {holidaySubBusy ? 'Wird abgeglichen …' : 'Abonnieren'}
                    </button>
                  </span>
                )}
              </div>
            )}
            {mayEditCatalog && (
              <div className="row end">
                <button className="btn primary sm" onClick={() => setModal('holiday')}>
                  <IconPlus width={14} height={14} /> Feiertag anlegen
                </button>
              </div>
            )}
            {holidays.loading ? (
              <Loading />
            ) : upcomingHolidays.length === 0 ? (
              <EmptyState
                icon={<IconCalendar width={20} height={20} />}
                title="Keine Feiertage hinterlegt"
                hint="An Feiertagen ruhen Fristen von SLA-Stufen, die an Geschäftszeiten gebunden sind."
              />
            ) : (
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Datum</th>
                      <th>Bezeichnung</th>
                      <th>Region</th>
                      <th>Status</th>
                      {mayEditCatalog && <th className="actions">Aktionen</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {upcomingHolidays.map((holiday: any) => {
                      const past = holiday.date < new Date().toISOString().slice(0, 10);
                      return (
                        <tr key={holiday.id} style={past ? { opacity: 0.55 } : undefined}>
                          <td className="mono small">{formatDate(holiday.date)}</td>
                          <td>
                            <strong>{holiday.name}</strong>
                          </td>
                          <td className="small subtle">{holiday.region ?? 'bundesweit'}</td>
                          <td>
                            <Badge variant={past ? '' : 'success'}>{past ? 'vergangen' : 'kommend'}</Badge>
                          </td>
                          {mayEditCatalog && (
                            <td className="actions">
                              <RowActions
                                label={holiday.name}
                                onEdit={() => setModal(`holiday-${holiday.id}`)}
                                deletePath={`/api/catalogs/holidays/${holiday.id}`}
                                onDeleted={() => holidays.reload()}
                              />
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {section === 'abwesenheiten' && (
          <div className="stack">
            <div className="row between">
              <span className="small subtle">
                Bestand aller Personen. Die eigene Abwesenheit pflegt jede Person selbst im Profil.
              </span>
              {mayEditAbsences && (
                <button className="btn primary sm" onClick={() => setModal('absence')}>
                  <IconPlus width={14} height={14} /> Abwesenheit erfassen
                </button>
              )}
            </div>

            <div className="grid cols-3">
              <StatCard
                label="Heute abwesend"
                value={runningAbsences.length}
                tone={runningAbsences.length > 0 ? 'warning' : 'success'}
                icon={<IconAlert width={17} height={17} />}
                hint={runningAbsences.map((entry: any) => `${entry.first_name} ${entry.last_name}`).join(', ') || 'niemand'}
              />
              <StatCard
                label="Erfasst insgesamt"
                value={(absences.data ?? []).length}
                tone="info"
                icon={<IconCalendar width={17} height={17} />}
              />
              <StatCard
                label="Mit Vertretung"
                value={(absences.data ?? []).filter((entry: any) => entry.substitute_user_id).length}
                tone="accent"
                icon={<IconCheck width={17} height={17} />}
                hint="Zuweisungen laufen weiter"
              />
            </div>

            {absences.loading ? (
              <Loading />
            ) : (absences.data ?? []).length === 0 ? (
              <EmptyState
                icon={<IconCalendar width={20} height={20} />}
                title="Keine Abwesenheiten erfasst"
                hint="Abwesende Personen werden in der Disposition nicht mehr vorgeschlagen."
              />
            ) : (
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Person</th>
                      <th>Grund</th>
                      <th>Von</th>
                      <th>Bis</th>
                      <th>Vertretung</th>
                      <th>Bemerkung</th>
                      {mayEditAbsences && <th className="actions">Aktionen</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {(absences.data ?? []).map((entry: any) => (
                      <tr key={entry.id}>
                        <td>
                          <strong>
                            {entry.first_name} {entry.last_name}
                          </strong>
                        </td>
                        <td>
                          <Badge variant="info">
                            {ABSENCE_TYPES[entry.type] ?? entry.type}
                          </Badge>
                        </td>
                        <td className="small">{formatDate(entry.start_date)}</td>
                        <td className="small">{formatDate(entry.end_date)}</td>
                        <td className="small subtle">
                          {entry.sub_first_name ? `${entry.sub_first_name} ${entry.sub_last_name}` : '–'}
                        </td>
                        <td className="small subtle truncate" style={{ maxWidth: 200 }}>
                          {entry.note ?? '–'}
                        </td>
                        {mayEditAbsences && (
                          <td className="actions">
                            <RowActions
                              label={`${entry.first_name} ${entry.last_name}`}
                              onEdit={() => setModal(`absence-${entry.id}`)}
                              deletePath={`/api/catalogs/absences/${entry.id}`}
                              confirmText={`Abwesenheit von ${entry.first_name} ${entry.last_name} löschen?`}
                              onDeleted={() => absences.reload()}
                            />
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {mayEditAbsences && (
              <Card
                title="Urlaubskalender-Abos"
                subtitle="Individuelle ical-URLs der Personen – jede Person pflegt ihre eigene im Profil, hier für alle einsehbar und bearbeitbar"
                noBody
              >
                {absenceSubscriptions.loading ? (
                  <Loading />
                ) : !(absenceSubscriptions.data ?? []).length ? (
                  <div className="card-body">
                    <span className="small subtle">Noch kein Abo hinterlegt.</span>
                  </div>
                ) : (
                  <div className="table-wrap">
                    <table className="data">
                      <thead>
                        <tr>
                          <th>Person</th>
                          <th>Kalender-URL</th>
                          <th>Status</th>
                          <th className="actions">Aktionen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {absenceSubscriptions.data.map((sub: any) => (
                          <tr key={sub.id}>
                            <td>
                              <strong>
                                {sub.first_name} {sub.last_name}
                              </strong>
                            </td>
                            <td className="mono small truncate" style={{ maxWidth: 320 }}>
                              {sub.ical_url}
                            </td>
                            <td>
                              {sub.last_error ? (
                                <Badge variant="danger">{sub.last_error}</Badge>
                              ) : (
                                <Badge variant="success">in Ordnung</Badge>
                              )}
                            </td>
                            <td className="actions">
                              <button className="btn sm ghost" onClick={() => setModal(`sub-${sub.user_id}`)}>
                                Bearbeiten
                              </button>
                              <button
                                className="btn sm ghost"
                                onClick={async () => {
                                  if (!window.confirm(`Abo von ${sub.first_name} ${sub.last_name} entfernen?`)) return;
                                  await api.delete(`/api/catalogs/absences/subscriptions/${sub.user_id}`);
                                  absenceSubscriptions.reload();
                                  absences.reload();
                                  notify('Abo entfernt.', 'success');
                                }}
                              >
                                Entfernen
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="card-body" style={{ borderTop: '1px solid var(--border)' }}>
                  <button className="btn sm" onClick={() => setModal('sub-new')}>
                    <IconPlus width={14} height={14} /> Abo für Person hinzufügen
                  </button>
                </div>
              </Card>
            )}
          </div>
        )}
      </Card>

      {(modal === 'sub-new' || modal?.startsWith('sub-')) && (
        <SubscriptionModal
          existing={(absenceSubscriptions.data ?? []).find((sub: any) => modal === `sub-${sub.user_id}`)}
          users={userList}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            absenceSubscriptions.reload();
            absences.reload();
            notify('Abo gespeichert.', 'success');
          }}
        />
      )}

      {modal?.startsWith('holiday') && (
        <HolidayModal
          holiday={(holidays.data ?? []).find((entry: any) => String(entry.id) === modal.replace('holiday-', ''))}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            holidays.reload();
            notify('Feiertag gespeichert.', 'success');
          }}
        />
      )}

      {modal?.startsWith('absence') && (
        <AbsenceModal
          absence={(absences.data ?? []).find((entry: any) => String(entry.id) === modal.replace('absence-', ''))}
          users={userList}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            absences.reload();
            notify('Abwesenheit gespeichert.', 'success');
          }}
        />
      )}
    </div>
  );
}

function HolidayModal({ holiday, onClose, onSaved }: { holiday?: any; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    name: holiday?.name ?? '',
    date: holiday?.date ?? '',
    region: holiday?.region ?? '',
  });
  const [error, setError] = useState<unknown>(null);

  return (
    <Modal
      title={holiday ? `Feiertag bearbeiten – ${holiday.name}` : 'Feiertag anlegen'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="btn primary"
            disabled={form.name.length < 2 || !form.date}
            onClick={async () => {
              try {
                const payload = { name: form.name, date: form.date, region: form.region || null };
                if (holiday) await api.patch(`/api/catalogs/holidays/${holiday.id}`, payload);
                else await api.post('/api/catalogs/holidays', payload);
                onSaved();
              } catch (caught) {
                setError(caught);
              }
            }}
          >
            Speichern
          </button>
        </>
      }
    >
      <div className="stack">
        <ErrorMessage error={error} />
        <Field label="Bezeichnung" required>
          <input value={form.name} autoFocus onChange={(event) => setForm({ ...form, name: event.target.value })} />
        </Field>
        <Field label="Datum" required>
          <input type="date" value={form.date} onChange={(event) => setForm({ ...form, date: event.target.value })} />
        </Field>
        <Field label="Region" hint="Leer lassen für bundesweit geltende Feiertage.">
          <input
            value={form.region}
            onChange={(event) => setForm({ ...form, region: event.target.value })}
            placeholder="z. B. Niedersachsen"
          />
        </Field>
      </div>
    </Modal>
  );
}

/**
 * Urlaubskalender-Abo im Namen einer Person (Admin-Sicht).
 *
 * Ergänzt die Selbstverwaltung im Profil – jede Person sieht dort nur ihr
 * eigenes Abo, hier lassen sich alle einsehen und im Namen einer Person
 * einrichten oder ändern.
 */
function SubscriptionModal({
  existing,
  users,
  onClose,
  onSaved,
}: {
  existing?: any;
  users: any[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [userId, setUserId] = useState(existing ? String(existing.user_id) : '');
  const [icalUrl, setIcalUrl] = useState(existing?.ical_url ?? '');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  return (
    <Modal
      title={existing ? `Abo bearbeiten – ${existing.first_name} ${existing.last_name}` : 'Abo für Person hinzufügen'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="btn primary"
            disabled={busy || !userId || !icalUrl.trim()}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await api.put(`/api/catalogs/absences/subscriptions/${userId}`, { icalUrl: icalUrl.trim() });
                onSaved();
              } catch (caught) {
                setError(caught);
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? 'Wird abgeglichen …' : 'Speichern'}
          </button>
        </>
      }
    >
      <div className="stack">
        <ErrorMessage error={error} />
        {!existing && (
          <Field label="Person" required>
            <SearchSelect
              value={userId}
              onChange={setUserId}
              options={(users ?? []).map((person: any) => ({
                value: String(person.id),
                label: `${person.first_name} ${person.last_name}`,
              }))}
              placeholder="– bitte wählen –"
            />
          </Field>
        )}
        <Field label="Kalender-URL" required>
          <input
            type="url"
            value={icalUrl}
            autoFocus
            onChange={(event) => setIcalUrl(event.target.value)}
            placeholder="https://calendar.google.com/…/basic.ics"
          />
        </Field>
      </div>
    </Modal>
  );
}

function AbsenceModal({
  absence,
  users,
  onClose,
  onSaved,
}: {
  absence?: any;
  users: any[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    userId: absence?.user_id ? String(absence.user_id) : '',
    type: absence?.type ?? 'urlaub',
    startDate: absence?.start_date ?? '',
    endDate: absence?.end_date ?? '',
    substituteUserId: absence?.substitute_user_id ? String(absence.substitute_user_id) : '',
    note: absence?.note ?? '',
  });
  const [error, setError] = useState<unknown>(null);

  const invalidRange = Boolean(form.startDate && form.endDate && form.endDate < form.startDate);

  return (
    <Modal
      title={absence ? 'Abwesenheit bearbeiten' : 'Abwesenheit erfassen'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="btn primary"
            disabled={!form.userId || !form.startDate || !form.endDate || invalidRange}
            onClick={async () => {
              try {
                const payload = {
                  userId: Number(form.userId),
                  type: form.type,
                  startDate: form.startDate,
                  endDate: form.endDate,
                  substituteUserId: form.substituteUserId ? Number(form.substituteUserId) : null,
                  note: form.note || null,
                };
                if (absence) await api.patch(`/api/catalogs/absences/${absence.id}`, payload);
                else await api.post('/api/catalogs/absences', payload);
                onSaved();
              } catch (caught) {
                setError(caught);
              }
            }}
          >
            Speichern
          </button>
        </>
      }
    >
      <div className="stack">
        <ErrorMessage error={error} />
        <div className="form-grid">
          <Field label="Person" required>
            <SearchSelect
              value={String(form.userId ?? '')}
              onChange={(wert) => setForm({ ...form, userId: wert })}
              options={(users ?? []).map((person: any) => ({ value: String(person.id), label: `${person.first_name} ${person.last_name}` }))}
              placeholder="– bitte wählen –"
            />
          </Field>
          <Field label="Grund" required>
            <select value={form.type} onChange={(event) => setForm({ ...form, type: event.target.value })}>
              {Object.entries(ABSENCE_TYPES).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Von" required>
            <input
              type="date"
              value={form.startDate}
              onChange={(event) => setForm({ ...form, startDate: event.target.value })}
            />
          </Field>
          <Field label="Bis" required>
            <input
              type="date"
              value={form.endDate}
              onChange={(event) => setForm({ ...form, endDate: event.target.value })}
            />
          </Field>
        </div>

        {invalidRange && <div className="alert warning">Das Enddatum liegt vor dem Startdatum.</div>}

        <Field label="Vertretung" hint="Übernimmt in dieser Zeit die Zuweisungen der abwesenden Person.">
          <SearchSelect
            value={String(form.substituteUserId ?? '')}
            onChange={(wert) => setForm({ ...form, substituteUserId: wert })}
            options={users
              .filter((person: any) => String(person.id) !== form.userId)
              .map((person: any) => ({ value: String(person.id), label: `${person.first_name} ${person.last_name}` }))}
            placeholder="Keine Vertretung"
          />
        </Field>
        <Field label="Bemerkung">
          <input value={form.note} onChange={(event) => setForm({ ...form, note: event.target.value })} />
        </Field>
      </div>
    </Modal>
  );
}
